import {
  type DiscountAvailabilityFilterContext,
  buildFilteredDiscountAvailabilityForRestaurant,
  sortDiscountAvailabilityByDate,
} from "./buildDiscountAvailabilityForRestaurant";
import {
  type DeriveCacheIdentity,
  type DiscountAvailabilityBundle,
  type DiscountAvailabilityCacheEntry,
  getDatesKey,
  getDiscountRestaurantCache,
  getNextDayRestaurantCache,
  getPreviousDiscountBundle,
  setPreviousDiscountBundle,
} from "./deriveAvailabilityCacheRegistry";
import { getCachedIsNextDiscountDayAvailable } from "./deriveIsNextDiscountDayAvailableById";
import {
  type DiscountAvailabilityStrategy,
  firstTableDiscountAvailabilityStrategy,
} from "./discountAvailabilityStrategy";
import { buildFirebaseAllAvailabilitySessionsById } from "./firebaseAllAvailabilityFetch";
import { type FirebaseAllAvailabilityById } from "../../model/firebaseAllAvailability";
import { type RestaurantDetailsById } from "../../model/restaurants/byId";
import {
  type ByRestaurantId,
  type DiscountAvailabilityByDate,
  type DiscountAvailabilityById,
} from "../../model/types";
import type { NonEmptyArray } from "../../types";

const getGlobalKey = (
  context: DiscountAvailabilityFilterContext,
  strategy: DiscountAvailabilityStrategy,
): string =>
  [
    strategy.modeKey,
    context.sessions.join(","),
    context.hasLastTable,
    context.timesStart,
    context.timesEnd,
    context.showAllDiscounts,
    context.people,
  ].join("\0");

const sameIdList = (a: number[], b: number[]): boolean =>
  a === b || (a.length === b.length && a.every((id, index) => id === b[index]));

const pruneCache = <T>(cache: Map<number, T>, activeIds: Set<number>): void => {
  for (const cachedId of cache.keys()) {
    if (!activeIds.has(cachedId)) {
      cache.delete(cachedId);
    }
  }
};

const padAvailabilityDates = (
  dates: readonly string[],
  placeholder: null | undefined,
): DiscountAvailabilityByDate =>
  dates.reduce((availabilityByDate: DiscountAvailabilityByDate, date) => {
    availabilityByDate[date] = placeholder;
    return availabilityByDate;
  }, {});

const getCachedRestaurantAvailability = (
  cache: Map<number, DiscountAvailabilityCacheEntry>,
  restaurantId: number,
  sourceRef: FirebaseAllAvailabilityById[number] | undefined,
  discountDatesToShow: readonly string[],
  context: DiscountAvailabilityFilterContext,
  strategy: DiscountAvailabilityStrategy,
  unresolvedPlaceholder: null | undefined,
): DiscountAvailabilityCacheEntry => {
  const datesKey = getDatesKey(discountDatesToShow);
  const globalKey = getGlobalKey(context, strategy);
  const cached = cache.get(restaurantId);

  if (
    cached &&
    cached.sourceRef === sourceRef &&
    cached.datesKey === datesKey &&
    cached.globalKey === globalKey &&
    cached.unresolvedPlaceholder === unresolvedPlaceholder
  ) {
    return cached;
  }

  const filtered = buildFilteredDiscountAvailabilityForRestaurant(
    sourceRef,
    discountDatesToShow,
    context,
    strategy,
    unresolvedPlaceholder,
  );
  const availability = filtered
    ? sortDiscountAvailabilityByDate(filtered, context.people, strategy)
    : padAvailabilityDates(discountDatesToShow, unresolvedPlaceholder);

  const nextEntry: DiscountAvailabilityCacheEntry = {
    sourceRef,
    datesKey,
    globalKey,
    unresolvedPlaceholder,
    availability,
  };

  cache.set(restaurantId, nextEntry);
  return nextEntry;
};

type DeriveDiscountAvailabilityBundleParams = {
  deriveCacheIdentity: DeriveCacheIdentity;
  restaurantIds: number[];
  firebaseAllAvailabilityById: FirebaseAllAvailabilityById;
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>;
  restaurantsById: RestaurantDetailsById;
  walkInEnabled: boolean;
  context: DiscountAvailabilityFilterContext;
  strategy?: DiscountAvailabilityStrategy;
};

/**
 * Filter → sort by-id + next-day in one pass. Skips restaurants whose
 * `byId[id]` sourceRef is unchanged (restaurant-scoped; see identity pipeline
 * on isSameAvailabilitySnapshot).
 */
export const deriveDiscountAvailabilityBundle = ({
  deriveCacheIdentity,
  restaurantIds,
  firebaseAllAvailabilityById,
  discountDatesToShowById,
  restaurantsById,
  walkInEnabled,
  context,
  strategy = firstTableDiscountAvailabilityStrategy,
}: DeriveDiscountAvailabilityBundleParams): DiscountAvailabilityBundle => {
  const restaurantCache = getDiscountRestaurantCache(deriveCacheIdentity);
  const nextDayCache = getNextDayRestaurantCache(deriveCacheIdentity);
  const activeIds = new Set(restaurantIds);

  pruneCache(restaurantCache, activeIds);
  pruneCache(nextDayCache, activeIds);

  const contextKey = getGlobalKey(context, strategy);
  const previousBundle = getPreviousDiscountBundle(deriveCacheIdentity);
  const canPatchPrevious =
    previousBundle !== undefined &&
    previousBundle.contextKey === contextKey &&
    previousBundle.discountDatesToShowById === discountDatesToShowById &&
    previousBundle.restaurantsByIdRef === restaurantsById &&
    sameIdList(previousBundle.restaurantIds, restaurantIds);

  if (
    canPatchPrevious &&
    previousBundle.byIdRef === firebaseAllAvailabilityById
  ) {
    return previousBundle.result;
  }

  const sessionsById = buildFirebaseAllAvailabilitySessionsById(
    restaurantIds,
    context.sessions,
    context.hasLastTable,
    restaurantsById,
    walkInEnabled,
  );

  const byId: DiscountAvailabilityById = canPatchPrevious
    ? { ...previousBundle.result.byId }
    : {};
  const nextDay: ByRestaurantId<boolean> = canPatchPrevious
    ? { ...previousBundle.result.nextDay }
    : {};
  let didChange = !canPatchPrevious;

  for (const restaurantId of restaurantIds) {
    const dates = discountDatesToShowById[restaurantId] ?? [];
    const sourceRef = firebaseAllAvailabilityById[restaurantId];
    const restaurant = restaurantsById[restaurantId];
    const unresolvedPlaceholder =
      restaurant === undefined
        ? undefined
        : (sessionsById[restaurantId]?.length ?? 0) === 0
          ? null
          : undefined;
    const cached = getCachedRestaurantAvailability(
      restaurantCache,
      restaurantId,
      sourceRef,
      dates,
      context,
      strategy,
      unresolvedPlaceholder,
    );
    const nextDayValue = getCachedIsNextDiscountDayAvailable(
      nextDayCache,
      restaurantId,
      sourceRef,
      dates,
      context.sessions,
      context.people,
      strategy,
    );

    if (byId[restaurantId] !== cached.availability) {
      byId[restaurantId] = cached.availability;
      didChange = true;
    }

    if (nextDay[restaurantId] !== nextDayValue) {
      nextDay[restaurantId] = nextDayValue;
      didChange = true;
    }
  }

  const result: DiscountAvailabilityBundle = { byId, nextDay };

  if (canPatchPrevious && !didChange) {
    setPreviousDiscountBundle(deriveCacheIdentity, {
      ...previousBundle,
      byIdRef: firebaseAllAvailabilityById,
    });
    return previousBundle.result;
  }

  setPreviousDiscountBundle(deriveCacheIdentity, {
    contextKey,
    restaurantIds,
    discountDatesToShowById,
    byIdRef: firebaseAllAvailabilityById,
    restaurantsByIdRef: restaurantsById,
    result,
  });

  return result;
};

export type { DiscountAvailabilityBundle };
